/**
 * Deterministic noise generation. Everything here is seeded from src/core/Rand.js;
 * there is no Math.random() anywhere in the audio module.
 *
 * Long noise beds are generated once into module-level Float32Arrays and wrapped
 * into per-context AudioBuffers on demand, so an OfflineAudioContext bake costs a
 * copy rather than a regeneration.
 */
import { makeRng } from '../../core/Rand.js';

export const SR = 48000;

/** Fill with white noise in [-1, 1]. */
export function fillWhite(out, rng) {
  for (let i = 0; i < out.length; i++) out[i] = rng() * 2 - 1;
  return out;
}

/** Paul Kellet's economy pink filter. Roughly -3 dB/octave. */
export function fillPink(out, rng) {
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < out.length; i++) {
    const w = rng() * 2 - 1;
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    out[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.2;
    b6 = w * 0.115926;
  }
  return normalise(out, 0.95);
}

/** Leaky-integrated white. -6 dB/octave; the substance under an explosion. */
export function fillBrown(out, rng) {
  let last = 0;
  for (let i = 0; i < out.length; i++) {
    const w = rng() * 2 - 1;
    last = (last + 0.028 * w) * 0.9985;
    out[i] = last;
  }
  return normalise(out, 0.95);
}

/**
 * Velvet noise — sparse ±1 impulses, one per `period` samples. The cheapest way
 * to get a perceptually smooth, dense-sounding diffuser without comb colouration.
 */
export function fillVelvet(out, rng, density = 1800) {
  out.fill(0);
  const period = Math.max(1, Math.round(out.length / Math.max(1, density)));
  for (let k = 0; k * period < out.length; k++) {
    const i = k * period + Math.floor(rng() * period);
    if (i < out.length) out[i] = rng() < 0.5 ? -1 : 1;
  }
  return out;
}

export function normalise(out, target = 1) {
  let p = 0;
  for (let i = 0; i < out.length; i++) { const a = Math.abs(out[i]); if (a > p) p = a; }
  if (p > 1e-9) { const g = target / p; for (let i = 0; i < out.length; i++) out[i] *= g; }
  return out;
}

/* ------------------------------------------------------------------ *
 * Shared beds, generated once at module load (~4 ms total).
 * ------------------------------------------------------------------ */

const BED_SECONDS = 2.0;
const BED_N = Math.floor(SR * BED_SECONDS);

let _white = null;
let _pink = null;
let _brown = null;

export function whiteBed() {
  if (!_white) _white = fillWhite(new Float32Array(BED_N), makeRng(0x5eed01));
  return _white;
}
export function pinkBed() {
  if (!_pink) _pink = fillPink(new Float32Array(BED_N), makeRng(0x5eed02));
  return _pink;
}
export function brownBed() {
  if (!_brown) _brown = fillBrown(new Float32Array(BED_N), makeRng(0x5eed03));
  return _brown;
}

export function bed(kind) {
  if (kind === 'pink') return pinkBed();
  if (kind === 'brown') return brownBed();
  return whiteBed();
}

/**
 * A slice of a shared noise bed as a fresh Float32Array, with a deterministic
 * random start offset. This is the round-robin trick: the same synthesis recipe
 * sounds different every shot because it eats a different chunk of noise.
 */
export function noiseSlice(kind, samples, rng) {
  const src = bed(kind);
  const out = new Float32Array(samples);
  if (samples <= src.length) {
    const start = Math.max(0, Math.floor(rng() * Math.max(1, src.length - samples)));
    out.set(src.subarray(start, start + samples), 0);
    return out;
  }
  // longer than the bed: wrap around from a random offset (still deterministic)
  const start = Math.floor(rng() * src.length);
  for (let i = 0; i < samples; i++) out[i] = src[(start + i) % src.length];
  return out;
}

/**
 * Wrap a Float32Array into an AudioBuffer belonging to `ac`.
 * Works identically on a live AudioContext and an OfflineAudioContext.
 */
export function toBuffer(ac, data, channels = 1) {
  const buf = ac.createBuffer(channels, data.length, ac.sampleRate);
  for (let c = 0; c < channels; c++) buf.copyToChannel(data, c);
  return buf;
}

/** Stereo AudioBuffer from two arrays. */
export function toStereoBuffer(ac, l, r) {
  const buf = ac.createBuffer(2, l.length, ac.sampleRate);
  buf.copyToChannel(l, 0);
  buf.copyToChannel(r, 1);
  return buf;
}

/** Per-context cache of a long looping noise bed (for wind, rumble, etc.). */
const bedCache = new WeakMap();
export function bedBuffer(ac, kind) {
  let m = bedCache.get(ac);
  if (!m) { m = new Map(); bedCache.set(ac, m); }
  if (!m.has(kind)) m.set(kind, toBuffer(ac, bed(kind)));
  return m.get(kind);
}
